// src/calculation/generateTables/cutUtils.js

export const TRIM = 10.0;
export const KERF = 4.4;
export const CutDirection = {
  HORIZONTAL: "HORIZONTAL",
  VERTICAL: "VERTICAL"
};

export function resolveCutLabel({
  vertical,
  isTrim,
  isSecondTrim,
  isInnerBandCut,
  isInnerBandCutX,
  rootMainDir
}) {
  if (isTrim) return isSecondTrim ? "RX" : "RY";

  const base =
    rootMainDir === "HORIZONTAL"
      ? vertical
        ? "Y"
        : "X"
      : vertical
        ? "X"
        : "Y";

  if (isInnerBandCut && base === "Y") return "U";
  if (isInnerBandCutX && base === "X") return "V";

  return base;
}
function parseKantFull(raw) {
  if (!raw) return { type: "-", longCount: 0, shortCount: 0 };

  try {
    let s = raw.trim();
    if (s.startsWith("[") && s.endsWith("]")) {
      s = s.slice(1, -1);
    }

    const parts = s.split(",");

    return {
      type: parts[0]?.trim() || "-",
      longCount: parseInt(parts[1] ?? "0", 10) || 0,
      shortCount: parseInt(parts[2] ?? "0", 10) || 0
    };
  } catch {
    return { type: "-", longCount: 0, shortCount: 0 };
  }
}
export function computeKantTotals(placedRects, createdItems) {
  const totals = new Map();
  totals.set("-", 0);

  for (const r of placedRects) {
    const createdItem = createdItems?.find(
      (o) => o.itemId === (r.mainid ?? r.localid)
    );

    if (!createdItem) {
      continue;
    }

    const kant = parseKantFull(createdItem.kant);
    const h =
      typeof r.height === "number"
        ? r.height
        : typeof r.ph === "number"
          ? r.ph
          : 0;

    const w =
      typeof r.width === "number"
        ? r.width
        : typeof r.pw === "number"
          ? r.pw
          : 0;

    const add = 1.1 * (kant.longCount * h + kant.shortCount * w);

    const prev = totals.get(kant.type ?? "-") ?? 0;
    const next = prev + add;

    totals.set(kant.type ?? "-", next);
  }

  const result = [...totals.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([t, len]) => `${t}:${len.toFixed(1)}`)
    .join("|");

  return result;
}

export function computeCutLength(mergedCuts, sheetW, sheetH) {
  const unique = new Set();
  let total = 0;

  for (const c of mergedCuts) {
    const token = c.label ?? (c.vertical ? "Y" : "X");
    const key = (c.vertical ? "Y@" : "X@") + Number(c.pos).toFixed(3);

    if (!unique.has(key)) {
      unique.add(key);

      if (token === "Y" || token === "RY" || token === "U") {
        total += sheetH;
      } else {
        total += sheetW;
      }
    }
  }

  return total;
}

export function buildRighe(cutsWithLabels) {
  let out = "[Righe]\n";
  // out += "NumeroRighe=" + cutsWithLabels.length + "\n";
  let lines = [];
  cutsWithLabels.forEach((c) => {
    // normál cut sor
    lines.push(
      `${c.label},${c.pos.toFixed(6)},${
        c.count ?? 1
      },0.000000,0.000000,0.000000,0.000000`
    );
    // ===== MARKER BESZÚRÁS =====
    if (c.releasesElements) {
      if (c.label === "U" || c.label === "Y") {
        lines.push("V*");
      } else if (c.label === "V" || c.label === "X") {
        lines.push("U*");
      }
    }
  });
  out += "NumeroRighe=" + lines.length + "\n";
  lines.forEach((line, i) => {
    out += `${i + 1}=${line}\n`;
  });
  return out;
}

export function buildRigheAbs(cutsWithLabels, originalCuts = cutsWithLabels) {
  const lines = [];
  const originalById = new Map(
    originalCuts.filter((cut) => cut.cutId != null).map((cut) => [cut.cutId, cut])
  );

  const geometry = (c) => {
    let label = c.label;

    /*
     * A normál belső vágások geometriai megfelelője.
     */
    if (label === "U") label = "Y";
    if (label === "V") label = "X";

    const absValue =
      c.abs !== null && c.abs !== undefined ? Number(c.abs) : Number(c.pos);

    const spanStart =
      c.spanStart !== null && c.spanStart !== undefined
        ? Number(c.spanStart)
        : 0;

    const spanEnd =
      c.spanEnd !== null && c.spanEnd !== undefined ? Number(c.spanEnd) : 0;

    return (
      `${label},` +
        `${absValue.toFixed(6)},` +
        `${spanStart.toFixed(6)},` +
        `${spanEnd.toFixed(6)},` +
        `1,0.000000,0.000000,0.000000,0.000000`
    );
  };

  cutsWithLabels.forEach((c) => {
    // One machine row can represent several physical cuts. Keep its row ID
    // aligned with Righe/Riferimenti and store every geometry in its payload.
    const originals = (c.cutIds ?? [c.cutId]).map(
      (id) => originalById.get(id)
    ).filter(Boolean);
    lines.push((originals.length ? originals : [c]).map(geometry).join("|"));

    /*
     * U* / V* marker ugyanabban a sorrendben,
     * mint a Righe szekcióban.
     */
    if (c.releasesElements) {
      if (c.label === "U" || c.label === "Y") {
        lines.push("V*");
      } else if (c.label === "V" || c.label === "X") {
        lines.push("U*");
      }
    }
  });

  let out = "[RigheAbs]\n";
  out += `NumeroRighe=${lines.length}\n`;

  lines.forEach((line, index) => {
    out += `${index + 1}=${line}\n`;
  });

  return out;
}

export function mergeCutSequences(cuts, pieceSizeByElement = null) {
  const result = [];
  let i = 0;
  const same = (a, b) => {
    // A removed band must be cut away separately, never stacked with a part.
    if (a.isWaste || b.isWaste) return false;
    if (
      a.label !== b.label ||
      a.vertical !== b.vertical ||
      Math.abs(a.pos - b.pos) >= 1e-6
    )
      return false;

    // ha nincs méret map, akkor csak geometria alapján merge
    if (!pieceSizeByElement) return true;

    const aSize = pieceSizeByElement.get(a.elementId);
    const bSize = pieceSizeByElement.get(b.elementId);

    if (!aSize || !bSize) return true;

    return aSize.w === bSize.w && aSize.h === bSize.h;
  };
  while (i < cuts.length) {
    // ---- ABAB pattern
    if (
      i + 3 < cuts.length &&
      !same(cuts[i], cuts[i + 1]) && // ez a kulcs
      same(cuts[i], cuts[i + 2]) &&
      same(cuts[i + 1], cuts[i + 3])
    ) {
      result.push({
        ...cuts[i],
        count: 2,
        cutIds: [cuts[i].cutId, cuts[i + 2].cutId],
        isLastInSector: cuts[i].isLastInSector || cuts[i + 2].isLastInSector
      });

      result.push({
        ...cuts[i + 1],
        count: 1,
        cutIds: [cuts[i + 1].cutId, cuts[i + 3].cutId],
        isLastInSector: cuts[i + 1].isLastInSector || cuts[i + 3].isLastInSector
      });

      i += 4;
      continue;
    }

    let repeat = 1;

    while (i + repeat < cuts.length && same(cuts[i], cuts[i + repeat])) {
      repeat++;
    }

    if (repeat > 1) {
      let remaining = repeat;

      while (remaining > 0) {
        const take = Math.min(2, remaining);

        const slice = cuts.slice(
          i + (repeat - remaining),
          i + (repeat - remaining) + take
        );

        result.push({
          ...slice[0],
          count: take,
          isLastInSector: slice.some((c) => c.isLastInSector),
          cutIds: slice.map((c) => c.cutId)
        });

        remaining -= take;
      }

      i += repeat;
      continue;
    }

    result.push({
      ...cuts[i],
      count: 1,
      cutIds: [cuts[i].cutId]
    });

    i++;
  }
  return mergeMatchingSectors(result, same);
}

// Y starts a full-width sector; U/V cuts belong to its inner sequence.
// Merge complete neighbouring sectors only, keeping the existing two-piece limit.
function mergeMatchingSectors(cuts, same) {
  const result = [];
  const sectorEnd = (start) => {
    let end = start + 1;
    while (end < cuts.length && cuts[end].label !== "Y") end++;
    return end;
  };
  const sameStep = (a, b) =>
    same(a, b) &&
    a.count === b.count &&
    a.level === b.level &&
    Boolean(a.isLastInSector) === Boolean(b.isLastInSector) &&
    Boolean(a.releasesElements) === Boolean(b.releasesElements) &&
    Boolean(a.forceReleaseMarker) === Boolean(b.forceReleaseMarker);

  let i = 0;
  while (i < cuts.length) {
    if (cuts[i].label !== "Y") {
      result.push(cuts[i++]);
      continue;
    }
    const end = sectorEnd(i);
    const nextEnd = end < cuts.length ? sectorEnd(end) : end;
    const length = end - i;
    if (
      length > 1 &&
      nextEnd - end === length &&
      cuts[i].count + cuts[end].count <= 2 &&
      cuts.slice(i, end).every((cut, offset) => sameStep(cut, cuts[end + offset]))
    ) {
      for (let offset = 0; offset < length; offset++) {
        const first = cuts[i + offset];
        const second = cuts[end + offset];
        result.push({
          ...first,
          count: offset === 0 ? first.count + second.count : first.count,
          cutIds: [...first.cutIds, ...second.cutIds]
        });
      }
      i = nextEnd;
    } else {
      result.push(...cuts.slice(i, end));
      i = end;
    }
  }
  return result;
}

function parseKant(raw) {
  if (!raw) return null;

  try {
    let s = raw.trim();
    if (s.startsWith("[") && s.endsWith("]")) {
      s = s.slice(1, -1);
    }

    const parts = s.split(",");

    const type = parts[0]?.trim();
    const longCount = parseInt(parts[1] ?? "0", 10);
    const shortCount = parseInt(parts[2] ?? "0", 10);

    return {
      type,
      longCount: isNaN(longCount) ? 0 : longCount,
      shortCount: isNaN(shortCount) ? 0 : shortCount
    };
  } catch {
    return null;
  }
}

export function buildDati({
  placedRects,
  createdItems,
  color,
  work,
  sheetWidth,
  sheetHeight,
  isRotated,
  isSplit,
  trim = TRIM,
  subtractKerf = true
}) {
  let out = "";

  // ===============================
  // INTESTAZIONE
  // ===============================
  const trimOffset = Math.max(0, Number(trim) || 0) * 2;
  let length = Number(sheetHeight) + trimOffset || 0;
  let width = Number(sheetWidth) + trimOffset || 0;
  if (isSplit && color?.splitDimension) {
    try {
      const dim = color.splitDimension
        .replace("[", "")
        .replace("]", "")
        .split(",");

      const l = parseFloat(dim[0]);
      const w = parseFloat(dim[1]);

      if (!isNaN(l)) length = l;
      if (!isNaN(w)) width = w;
    } catch {}
  }

  if (isRotated) {
    const tmp = length;
    length = width;
    width = tmp;
  }

  out += "[Intestazione]\n";
  out += `Descrizione=${color?.code ?? ""}\n`;
  out += `Lunghezza=${length.toFixed(6)}\n`;
  out += `Larghezza=${width.toFixed(6)}\n`;
  out += `Spessore=18.000000\n`;
  out += `AltPacco=36.000000\n\n\n`;

  // ===============================
  // DATI
  // ===============================
  out += "[Dati]\n";
  out += `NumeroDati=${placedRects.length}\n`;

  placedRects.forEach((r, i) => {
    const idx = i + 1;
    r.datiRowNumber = idx;
    /*
    const createdItem = createdItems?.find(
      (o) => (o.mainid ?? o.localid) === (r.mainid ?? r.localid)
    );*/
    const kant = parseKant(r.createdItem?.kant);
    const details = r.createdItem?.details || "";
    let sKantString = "0-0";
    let sFlag = "";
    let gKantString = "0-0";
    let gFlag = "";

    if (kant && kant.type === "04") {
      sKantString = `${kant.longCount}-${kant.shortCount}`;
      sFlag = "S";
    }
    if (kant && kant.type === "2") {
      gKantString = `${kant.longCount}-${kant.shortCount}`;
      gFlag = "G";
    }
    const colorName = color?.name ?? "";
    const colorDesc = color?.name ?? "";
    const workName = `${work?.user?.name ?? ""} ${color?.name ?? ""} M:${r.millingId ?? "-"}`;
    const rawHeight =
      typeof r.height === "number"
        ? r.height
        : typeof r.ph === "number"
          ? r.ph
          : 0;

    const rawWidth =
      typeof r.width === "number"
        ? r.width
        : typeof r.pw === "number"
          ? r.pw
          : 0;

    const kerfCorrection = subtractKerf ? KERF : 0;

    const h = Math.max(0, rawHeight - kerfCorrection);
    const w = Math.max(0, rawWidth - kerfCorrection);

    out +=
      `${idx}=,,1,,` +
      `${colorName},` +
      `${colorDesc},` +
      `${h.toFixed(1)},` +
      `${w.toFixed(1)},` +
      `${sKantString},` +
      `${gKantString},` +
      `${sFlag},` +
      `${gFlag},` +
      `${r.mainid ?? r.localid ?? ""},` +
      `${details},` +
      `${workName},,,,,,,,\n`;
  });

  return out;
}

// ============================
// RIFERIMENTI
// ============================

export function buildRiferimenti({ mergedCuts, placedRects }) {
  let out = "[Riferimenti]\n";

  let lines = [];

  mergedCuts.forEach((cut) => {
    const ids = cut.cutIds ?? [cut.cutId];

    let releasedRects = [];

    ids.forEach((id) => {
      placedRects.forEach((r) => {
        if (r.releaseCutId === id) {
          releasedRects.push(r);
        }
      });
    });

    releasedRects = [...new Set(releasedRects)];

    const releasesAny = releasedRects.length > 0;
    const allAreNonLast =
      releasesAny && releasedRects.every((r) => r.isLastInSector === false);

    cut.releasesElements =
      releasesAny && (cut.forceReleaseMarker === true || allAreNonLast);

    const refString = releasedRects
      .map((r) => `(${placedRects.indexOf(r) + 1})`)
      .join("");

    // ---- normál cut sor referencia
    lines.push(refString);

    // ---- marker sor referencia (üres)
    if (cut.releasesElements) {
      lines.push("");
    }
  });

  lines.forEach((ref, i) => {
    out += `${i + 1}=${ref}\n`;
  });

  return out;
}
